class BackgroundTimer {
	constructor() {
		this.timers = new Map();
		this.init();
	}

	init() {
		// Check for existing timers when the page loads
		this.checkExistingTimers();

		// Set up a global interval to check timers every second
		setInterval(() => {
			this.updateAllTimers();
		}, 1000);
	}

	startTimer(contestID, duration) {
		const startTime = Date.now();

		const timerData = {
			contestID,
			startTime,
			duration,
			isRunning: true,
			isPaused: false,
			pausedTime: 0,
			pausedDuration: 0,
		};

		this.timers.set(contestID, timerData);

		// Store in localStorage for persistence
		localStorage.setItem(
			`partyManagerTimer_${contestID}`,
			JSON.stringify(timerData)
		);
		localStorage.setItem(`partyManagerTimerRunning_${contestID}`, "true");
		localStorage.setItem(`partyManagerTimerPaused_${contestID}`, "false");

		return timerData;
	}

	pauseTimer(contestID) {
		const timer = this.timers.get(contestID);
		if (timer && timer.isRunning) {
			timer.isRunning = false;
			timer.isPaused = true;
			timer.pausedTime = Date.now();

			// Update localStorage
			localStorage.setItem(
				`partyManagerTimer_${contestID}`,
				JSON.stringify(timer)
			);
			localStorage.setItem(`partyManagerTimerRunning_${contestID}`, "false");
			localStorage.setItem(`partyManagerTimerPaused_${contestID}`, "true");
		}
	}

	resumeTimer(contestID) {
		const timer = this.timers.get(contestID);
		if (timer && timer.isPaused) {
			const pauseDuration = Date.now() - timer.pausedTime;
			timer.pausedDuration += pauseDuration;
			timer.isRunning = true;
			timer.isPaused = false;
			timer.pausedTime = 0;

			// Update localStorage
			localStorage.setItem(
				`partyManagerTimer_${contestID}`,
				JSON.stringify(timer)
			);
			localStorage.setItem(`partyManagerTimerRunning_${contestID}`, "true");
			localStorage.setItem(`partyManagerTimerPaused_${contestID}`, "false");
		}
	}

	stopTimer(contestID) {
		this.timers.delete(contestID);

		// Clear localStorage
		localStorage.removeItem(`partyManagerTimer_${contestID}`);
		localStorage.removeItem(`partyManagerTimerRunning_${contestID}`);
		localStorage.removeItem(`partyManagerTimerPaused_${contestID}`);
	}

	getTimer(contestID) {
		return this.timers.get(contestID);
	}

	getRemainingTime(contestID) {
		const timer = this.timers.get(contestID);
		if (!timer) {
			return 0;
		}

		if (timer.isPaused) {
			// For paused timers, calculate remaining time from when it was paused
			const elapsedBeforePause = Math.floor(
				(timer.pausedTime - timer.startTime) / 1000
			);
			const remaining = Math.max(0, timer.duration - elapsedBeforePause);
			return remaining;
		}

		if (timer.isRunning) {
			// For running timers, calculate remaining time from current time
			const elapsed = Math.floor((Date.now() - timer.startTime) / 1000);
			const totalPausedTime = Math.floor((timer.pausedDuration || 0) / 1000);
			const actualElapsed = elapsed - totalPausedTime;
			const remaining = Math.max(0, timer.duration - actualElapsed);
			return remaining;
		}

		return 0;
	}

	checkExistingTimers() {
		// Check localStorage for existing timers
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (
				key &&
				key.startsWith("partyManagerTimer_") &&
				!key.includes("Running") &&
				!key.includes("Paused")
			) {
				try {
					const timerData = JSON.parse(localStorage.getItem(key));

					if (timerData && timerData.isRunning && !timerData.isPaused) {
						// Check if timer should still be running
						const elapsed = Math.floor(
							(Date.now() - timerData.startTime) / 1000
						);
						const totalPausedTime = Math.floor(
							(timerData.pausedDuration || 0) / 1000
						);
						const actualElapsed = elapsed - totalPausedTime;

						if (actualElapsed < timerData.duration) {
							// Timer should still be running, restore it
							this.timers.set(timerData.contestID, timerData);
						} else {
							// Timer completed, remove it
							localStorage.removeItem(key);
						}
					} else if (timerData && timerData.isPaused) {
						// Paused timers can always be restored
						this.timers.set(timerData.contestID, timerData);
					}
				} catch (error) {
					console.error("Error parsing timer data:", error);
					localStorage.removeItem(key);
				}
			}
		}
	}

	updateAllTimers() {
		for (const [contestID, timer] of this.timers) {
			if (timer.isRunning && !timer.isPaused) {
				const elapsed = Math.floor((Date.now() - timer.startTime) / 1000);
				const totalPausedTime = Math.floor((timer.pausedDuration || 0) / 1000);
				const actualElapsed = elapsed - totalPausedTime;

				if (actualElapsed >= timer.duration) {
					// Timer completed
					timer.isRunning = false;

					// Remove from localStorage
					localStorage.removeItem(`partyManagerTimer_${contestID}`);
					localStorage.removeItem(`partyManagerTimerRunning_${contestID}`);
					localStorage.removeItem(`partyManagerTimerPaused_${contestID}`);

					// Remove from active timers
					this.timers.delete(contestID);

					// Dispatch custom event for timer completion
					window.dispatchEvent(
						new CustomEvent("timerCompleted", {
							detail: { contestID },
						})
					);
				}
			}
		}
	}

	// Method to clear all timer data (useful for debugging)
	clearAllTimers() {
		// Clear active timers
		this.timers.clear();

		// Clear localStorage timer data
		const keysToRemove = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && key.startsWith("partyManagerTimer_")) {
				keysToRemove.push(key);
			}
		}

		keysToRemove.forEach((key) => {
			localStorage.removeItem(key);
		});
	}
}

// Create and export a singleton instance
const backgroundTimer = new BackgroundTimer();
export default backgroundTimer;
